// React
import React from "react";
import { useHistory } from "react-router";
// Utils
import { BATCH_VIEW, GROUP_VIEW } from "constants/Routes";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { GROUP_CATEGORY_OPTIONS } from "constants/Groups";

// Hooks
import { useFormatDate, useGetCurrentBusinessUnit, useHasFeature } from "hooks";
// Components
import { AnimalsSummaryCard } from "components/MyLivestock";
// Types
import { KeyValuePairProps } from "components/Common/KeyValuePair";
import { GroupType } from "hooks/useGetGroups";
// Styles
import styles from "./GroupItem.module.scss";

interface GroupItemProps {
  group: GroupType;
}

export const GroupItem: React.FC<GroupItemProps> = ({ group }) => {
  const { weightUnits } = useGetCurrentBusinessUnit();
  const history = useHistory();
  const showRedesignedGroups = useHasFeature("SHOW_GROUP_REDESIGN");
  const { getFormatDate } = useFormatDate();

  const {
    animalsNumberCache,
    animalsToSaleCount = 0,
    avgDlwg,
    avgEstimatedWeight,
    avgRecordedWeight,
    category,
    daysTillTarget,
    name,
    summaryMetrics = [],
    targetDate,
    targetWeight,
    isBatch,
    numberOfUnidentifiedAnimalsInBatch,
  } = {
    ...group,
  };
  const categoryLabel: string | undefined = GROUP_CATEGORY_OPTIONS.find(
    (item) => item.value === (category as string),
  )?.label;
  const groupDetailsPath = `${isBatch ? BATCH_VIEW : GROUP_VIEW}/${group?.id}`;

  const handleGroupItemClick = (): void => history.push(groupDetailsPath);
  // we love foggles
  if (showRedesignedGroups) {
    const groupKeyData: KeyValuePairProps[] = [
      { label: "Target weight", unit: weightUnits, value: targetWeight },
      { label: "Avg. DLWG", unit: weightUnits, value: avgDlwg },
      { label: "Avg. estimated weight", unit: weightUnits, value: avgEstimatedWeight },
      {
        label: "Avg. recorded weight",
        unit: weightUnits,
        value: avgRecordedWeight,
      },
    ];

    const breedSexData = summaryMetrics?.map((item) => ({
      breedCode: item?.breedrCode || "",
      speciesColour: group?.animalType?.colour,
      speciesIconUrl: group?.animalType?.image || undefined,
      totalAnimals: item?.animalsCount || 0,
      totalFemale: item?.femaleCount || 0,
      totalMale: item?.maleCount || 0,
    }));

    const totalAnimals =
      animalsNumberCache && numberOfUnidentifiedAnimalsInBatch
        ? animalsNumberCache + numberOfUnidentifiedAnimalsInBatch
        : animalsNumberCache || numberOfUnidentifiedAnimalsInBatch || 0;

    return (
      <AnimalsSummaryCard
        group={group}
        breedSummaryData={breedSexData}
        keyValueData={groupKeyData}
        onClickUrl={groupDetailsPath}
        subtitle={`${totalAnimals} Animal${totalAnimals !== 1 ? "s" : ""} - ${categoryLabel}`}
        title={`${name}`}
      />
    );
  }

  return (
    <div id={ELEMENTS_IDS.GROUP_ITEM_BLOCK} className={styles.groupItem} onClick={handleGroupItemClick}>
      <span className={styles.groupName}>{name}</span>

      <div className={styles.infoBlock}>
        <span className={styles.infoItem}>
          <b>{animalsNumberCache}</b> animals in this group
        </span>

        <span className={styles.infoItem}>
          Target weight: <b>{targetWeight || "\u2014"}</b>
        </span>

        <span className={styles.infoItem}>
          Target date: <b>{getFormatDate(targetDate)}</b>
        </span>
        <span className={styles.infoItem}>
          Days till target date: <b>{daysTillTarget || "\u2014"}</b>
        </span>

        <span className={styles.infoItem}>{`${animalsToSaleCount} of ${animalsNumberCache} committed to sale`}</span>

        <span className={styles.infoItem}>
          Type: <b>{categoryLabel || "\u2014"}</b>
        </span>

        <span className={styles.infoItem}>
          Average estimated weight: <b>{+avgEstimatedWeight || "\u2014"}</b>
        </span>

        <span className={styles.infoItem}>
          Average recorded weight: <b>{+avgRecordedWeight || "\u2014"}</b>
        </span>
        <span className={styles.infoItem}>
          Average DLWG: <b>{+avgDlwg || "\u2014"}</b>
        </span>
      </div>
    </div>
  );
};
